// SB6-ANA-FILT-06 (D4) — the L3 "Kind" filter level.
//
// Per mockups (.feedback/26-05-27/L3 Kind Filter v2{, - Filter Hover
// State}.jpg) each Kind option button (`AI (181)`, `Defect (0)`) carries
// an inner **sliders icon** as a SECOND, independent click target:
//
//   ┌ L3 · KIND ⓘ ─────────────────────────┐
//   │ [ AI (181)  ⚙ ] [ Defect (0)  ⚙ ]      │
//   │ ⓘ No kind filter                       │
//   └────────────────────────────────────────┘
//
//   * the MAIN body toggles the kind selection (unchanged from L3 today);
//   * the INNER sliders icon OPENS the matching advanced widget
//     (AI Filter Module for `ai`, Defect Filter Widget for `defect`) and
//     does NOT toggle the option.
//
// Accessibility: we must NOT nest a <button> inside a <button>. The MUI
// `ToggleButton` already renders a <button>, so the inner icon CANNOT be
// a child <button>. Instead each option renders as a sibling layout: a
// `ToggleButton` (toggle target) + a separate `IconButton` (open target),
// grouped in a bordered pill so they read as one control. Both are real
// focusable controls with their own aria-labels.
import { Box, IconButton, Stack, ToggleButton, Tooltip } from "@mui/material";
import type { FacetAxis } from "@aeriosiq/types";
import { BypassHintSlot } from "./BypassHint.js";
import { LevelHeader } from "./LevelHeader.js";
import {
  FILTER_LEVEL_BYPASS,
  FILTER_LEVEL_INFO,
  FILTER_LEVEL_TITLES,
} from "./infoCopy.js";
import { SlidersHorizontalIcon } from "@aeriosiq/ui/icons";

export type KindOptionKey = "ai" | "defect";

interface KindLevelProps {
  axis: FacetAxis;
  /** Current selection for the kind axis (option keys). */
  value: readonly string[];
  onChange: (next: readonly string[]) => void;
  /** Open the advanced widget for the given kind (does not toggle). */
  onOpenAdvanced: (kind: KindOptionKey) => void;
}

const ADVANCED_LABEL: Record<KindOptionKey, string> = {
  ai: "AI Filter Module",
  defect: "Defect Filter Widget",
};

export function KindLevel({
  axis,
  value,
  onChange,
  onOpenAdvanced,
}: KindLevelProps): React.ReactElement {
  const title = FILTER_LEVEL_TITLES.kind ?? { prefix: "L3", label: "Kind" };
  const info = FILTER_LEVEL_INFO.kind ?? "";
  const levelDisabled = axis.disabled;
  const isEmpty = value.length === 0;
  const bypassLabel = FILTER_LEVEL_BYPASS.kind ?? "No kind filter";

  const toggle = (key: string): void => {
    const next = value.includes(key)
      ? value.filter((v) => v !== key)
      : [...value, key];
    onChange(next);
  };

  return (
    <Box
      data-testid="filter-level-kind"
      data-axis="kind"
      data-level-disabled={levelDisabled ? "true" : "false"}
      role="group"
      aria-label="Kind filter"
      sx={{ display: "flex", flexDirection: "column", gap: 0 }}
    >
      <LevelHeader
        prefix={title.prefix}
        label={title.label}
        info={info}
        axisKey="kind"
      />
      <Stack direction="row" spacing={0.5}>
        {axis.options.map((opt) => {
          const kind = opt.key as KindOptionKey;
          const selected = value.includes(opt.key);
          const optDisabled = opt.disabled || levelDisabled;
          return (
            <Box
              key={opt.key}
              data-testid={`filter-kind-option-${opt.key}`}
              sx={(theme) => ({
                display: "inline-flex",
                alignItems: "stretch",
                borderRadius: 1,
                overflow: "hidden",
                border: `1px solid ${
                  selected ? theme.palette.primary.main : theme.palette.divider
                }`,
                backgroundColor: selected
                  ? theme.palette.mode === "dark"
                    ? `${theme.palette.primary.main}33`
                    : `${theme.palette.primary.main}1F`
                  : "transparent",
                opacity: optDisabled ? 0.5 : 1,
              })}
            >
              {/* Toggle target — the option's main body. */}
              <ToggleButton
                value={opt.key}
                selected={selected}
                disabled={optDisabled}
                size="small"
                data-filter-value={opt.key}
                data-option-disabled={opt.disabled ? "true" : "false"}
                aria-pressed={selected}
                onChange={() => {
                  toggle(opt.key);
                }}
                sx={(theme) => ({
                  border: "none",
                  borderRadius: 0,
                  textTransform: "none",
                  px: 1.25,
                  py: 0.5,
                  color:
                    selected && theme.palette.mode !== "dark"
                      ? "#0058B8"
                      : selected
                        ? theme.palette.primary.main
                        : "text.primary",
                  "&.Mui-selected, &.Mui-selected:hover": {
                    backgroundColor: "transparent",
                  },
                })}
              >
                {opt.label}
                <Box
                  component="span"
                  data-testid={`filter-count-kind-${opt.key}`}
                  sx={{
                    ml: 0.5,
                    fontSize: 11,
                    color: "text.primary",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  ({opt.count.toString()})
                </Box>
              </ToggleButton>
              {/* Open target — the inner sliders icon. Independent control;
                  opens the advanced widget and does NOT toggle. Hover/focus
                  highlights it blue (mockup hover state). */}
              <Tooltip title={`Open ${ADVANCED_LABEL[kind]}`}>
                <span style={{ display: "inline-flex" }}>
                  <IconButton
                    size="small"
                    data-testid={`filter-kind-advanced-${opt.key}`}
                    aria-label={`Open ${ADVANCED_LABEL[kind]}`}
                    disabled={levelDisabled}
                    onClick={(e) => {
                      // Guard against any bubbling into the toggle.
                      e.stopPropagation();
                      onOpenAdvanced(kind);
                    }}
                    sx={(theme) => ({
                      borderRadius: 0,
                      borderLeft: `1px solid ${theme.palette.divider}`,
                      px: 0.5,
                      color: "text.secondary",
                      "&:hover, &:focus-visible": {
                        color: "primary.main",
                        backgroundColor: `${theme.palette.primary.main}14`,
                      },
                    })}
                  >
                    <SlidersHorizontalIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        })}
      </Stack>
      <BypassHintSlot show={isEmpty} label={bypassLabel} />
    </Box>
  );
}
